import { ImageOverlay, MapContainer, useMap } from 'react-leaflet';
import { MapPin } from 'src/schema';
import { useLocation, useNavigate } from 'react-router-dom';
import L from 'leaflet';
import { useContext, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

// everything will look wrong if this import is missing
import 'leaflet/dist/leaflet.css';

import StationMarkerGroup from './StationMarkerGroup';
import { useEventMap, MapContext } from 'src/context';
import LitterSweepLayer from './LitterSweepLayer';
import SweepTodosLayer from './SweepTodosLayer';

// ref to track what we've zoomed to
const zoomedToRef = { current: null as string | null };

interface Props {
    imgUrl: string;
    imgWidth: number;
    imgHeight: number;
    viewWidth: number;
    viewHeight: number;
}

interface SweepArea {
    bounds: L.LatLngBounds | { _southWest: { lat: number; lng: number }; _northEast: { lat: number; lng: number } };
    mapNumber: number;
}

interface MapEffectProps {
    pin?: MapPin | null;
    sweepArea?: SweepArea | null;
    targetMapId?: string;
    currentMapId: string;
    overlayWidth: number;
    overlayHeight: number;
}

function MapEffect({ pin, sweepArea, targetMapId, currentMapId, overlayWidth, overlayHeight }: MapEffectProps) {
    const map = useMap();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (pin) {
            if (targetMapId && targetMapId !== currentMapId) {
                return; // wait for the correct map to load
            }
            if (!targetMapId) {
                console.warn('MapEffect: target mapid is missing for pin zoom', pin);
                return;
            }
        }

        let zoomId: string | null = null;
        if (pin) {
            zoomId = `pin-${pin.id}`;
        } else if (sweepArea) {
            zoomId = `sweep-${sweepArea.mapNumber}`;
        }

        if (zoomId && zoomedToRef.current !== zoomId) {
            if (pin) {
                // Transform pin coordinates to leaflet coordinate space
                const lat = (pin.y as number) * overlayHeight;
                const lng = (pin.x as number) * overlayWidth;

                map.flyTo([lat, lng], 3, {
                    duration: 1.5,
                });
            } else if (sweepArea) {
                const bounds = sweepArea.bounds;
                let latLngBounds: L.LatLngBounds;

                if ('_southWest' in bounds && '_northEast' in bounds) {
                    latLngBounds = L.latLngBounds(
                        [bounds._southWest.lat, bounds._southWest.lng],
                        [bounds._northEast.lat, bounds._northEast.lng]
                    );
                } else {
                    latLngBounds = bounds as L.LatLngBounds;
                }

                map.fitBounds(latLngBounds, { padding: [50, 50], duration: 1.5 });
            }

            //mark as zoomed
            zoomedToRef.current = zoomId;

            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [pin, sweepArea, currentMapId, targetMapId, overlayWidth, overlayHeight, map, navigate, location.pathname]);

    return null;
}

export default function EventMapInner(props: Props) {
    const { imgUrl, imgWidth, imgHeight, viewWidth, viewHeight } = props;
    const location = useLocation();
    const pin = location.state?.pin as MapPin | undefined;
    const sweepArea = location.state?.sweepArea as SweepArea | undefined;
    const targetMapId = location.state?.mapId as string | undefined;
    const { mapNumber, eventId, currentTodoType, sweepRect, setSweepRect } = useContext(MapContext);
    const { map, isLoading, error } = useEventMap(eventId, mapNumber);

    useEffect(() => {
        setSweepRect(null);
    }, [mapNumber, setSweepRect]);

    // clear zoom
    useEffect(() => {
        return () => {
            zoomedToRef.current = null;
        };
    }, []);

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Typography color="error">Error loading map data: {error.message}</Typography>;
    }

    if (!map) {
        return <Typography>Map not found</Typography>;
    }

    // Calculate the largest we can zoom the image
    // while the whole thing fits in the screen.
    const magnification = Math.min(viewWidth / imgWidth, viewHeight / imgHeight);

    // final dimensions of displayed image
    const overlayWidth = magnification * imgWidth;
    const overlayHeight = magnification * imgHeight;

    const bounds = L.latLngBounds([0, 0], [overlayHeight, overlayWidth]);
    return (
        <MapContainer
            style={{ width: '100%', height: '100%' }}
            scrollWheelZoom={true}
            attributionControl={false}
            zoomControl={false}
            crs={L.CRS.Simple}
            bounds={bounds}
            minZoom={0}
            maxZoom={5}
        >
            <ImageOverlay url={imgUrl} bounds={bounds} />
            <StationMarkerGroup bounds={bounds} magnification={magnification} />
            <SweepTodosLayer />
            {map && (
                <MapEffect
                    pin={pin}
                    sweepArea={sweepArea}
                    targetMapId={targetMapId}
                    currentMapId={map.id}
                    overlayWidth={overlayWidth}
                    overlayHeight={overlayHeight}
                />
            )}
            {currentTodoType?.interactionType === 'draw-area' && <LitterSweepLayer rect={sweepRect} onRectChange={setSweepRect} />}
        </MapContainer>
    );
}
